import { useState } from "react";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { CalendarClock, Check, Link2, ShieldCheck, UserRound } from "lucide-react";
import type { RescueCase, UpdateRescueCommitmentRequest } from "@io-spoke/contracts/rescue";
import { Button } from "@components/ui/button";
import { Badge } from "@components/ui/badge";

const statusLabel = { at_risk: "At risk", verified: "Verified", fulfilled: "Fulfilled", broken: "Broken" };

function shortDate(value: string | null) {
  if (!value) return "No deadline found";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
}

export function PromiseLedger({ rescueCase, updatingId, onUpdate }: {
  rescueCase: RescueCase;
  updatingId: string | null;
  onUpdate: (commitmentId: string, update: UpdateRescueCommitmentRequest) => void | Promise<void>;
}) {
  const updateAction = useAsyncAction();
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const updatePromise = (id: string, action: "assign" | "verify" | "fulfill", update: UpdateRescueCommitmentRequest) => updateAction.run(async () => {
    setActiveAction(`${id}:${action}`);
    setError(null);
    try { await onUpdate(id, update); }
    catch { setError("The promise could not be updated. Try again."); }
    finally { setActiveAction(null); }
  });
  const busy = updatingId !== null || updateAction.pending;
  return (
    <section aria-labelledby="promise-ledger-title" className="border-b px-5 py-5 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-violet-600 dark:text-violet-400" aria-hidden="true" /><h3 id="promise-ledger-title" className="text-sm font-semibold">Promise Ledger</h3></div>
          <p className="mt-1 text-xs text-muted-foreground">Commitments reconstructed from customer evidence—not manually entered tasks.</p>
        </div>
        <Badge variant="outline">{rescueCase.commitments.length} {rescueCase.commitments.length === 1 ? "commitment" : "commitments"}</Badge>
      </div>
      {error ? <p role="alert" className="mt-3 text-sm text-destructive">{error}</p> : null}
      <div className="mt-3 overflow-hidden rounded-md border">
        {rescueCase.commitments.map((commitment) => {
          const sources = rescueCase.evidence.filter((item) => commitment.evidenceIds.includes(item.id));
          return (
            <article key={commitment.id} className="bg-card p-4">
              <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2"><Badge variant={commitment.status === "broken" ? "destructive" : "secondary"}>{statusLabel[commitment.status]}</Badge><span className="text-xs text-muted-foreground">{Math.round(commitment.confidence * 100)}% confidence</span></div>
                  <p className="mt-2 text-sm font-medium leading-6">{commitment.statement}</p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5" />{commitment.owner ?? "Owner missing"}</span>
                    <span className="inline-flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />{shortDate(commitment.dueAt)}</span>
                    <span className="inline-flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" />{sources.length} linked {sources.length === 1 ? "source" : "sources"}</span>
                  </div>
                  {sources.length > 0 ? <p className="mt-2 text-xs text-muted-foreground">Evidence: {sources.map((item) => item.sourceLabel).join(" · ")}</p> : null}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {!commitment.owner ? <Button size="sm" variant="outline" disabled={busy} loading={updateAction.pending && activeAction === `${commitment.id}:assign`} loadingLabel="Updating promise…" onClick={() => void updatePromise(commitment.id, "assign", { owner: "Customer Success" })}>Assign owner</Button> : null}
                  {commitment.status === "at_risk" ? <Button size="sm" variant="outline" disabled={busy} loading={updateAction.pending && activeAction === `${commitment.id}:verify`} loadingLabel="Updating promise…" onClick={() => void updatePromise(commitment.id, "verify", { status: "verified" })}><Check className="h-3.5 w-3.5" /> Verify</Button> : null}
                  {commitment.status === "verified" ? <Button size="sm" variant="outline" disabled={busy} loading={updateAction.pending && activeAction === `${commitment.id}:fulfill`} loadingLabel="Updating promise…" onClick={() => void updatePromise(commitment.id, "fulfill", { status: "fulfilled" })}><Check className="h-3.5 w-3.5" /> Mark fulfilled</Button> : null}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
